// "설정" — 원격 입력 제어 · 무인 접속 · 로그인하면 자동 실행(macOS) · 조직에 등록.
// 첫 화면에서는 오른쪽 서랍 메뉴에, 로그인 후 "연결" 화면에서는 기기 정보 아래에 그린다(2026-09-27 개편 — `RSP 화면.pdf`).
// On/Off 는 모두 공용 슬라이드 스위치(`ui/Switch`). 상태·명령은 `useMyConnection`(종전 `MyConnectionPanel` 로직 그대로).
import { useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { IS_SHELL, SHELL } from '../../shell';
import { Switch } from '../../ui/Switch';
import { confirmAction } from '../../ui/confirm';
import { PasswordField } from '../PasswordField';
import type { MyConnection } from './useMyConnection';

export function MyConnectionSettings({ conn }: { conn: MyConnection }) {
  const c = conn;
  /**
   * 무인 접속 스위치를 **켜는 중**(아직 비밀번호를 정하지 않음). 스위치는 켜진 모양으로 두고 비밀번호 칸을 연다.
   * 비밀번호가 실제로 설정되면(`unattendedSet`) 이 값은 할 일을 다 했다 → 내린다. 정하지 않고 끄면 그냥 접는다.
   */
  const [enabling, setEnabling] = useState(false);
  useEffect(() => {
    if (c.unattendedSet) setEnabling(false);
  }, [c.unattendedSet]);

  if (!IS_SHELL || !c.deviceCode) return null;
  const unattOn = c.unattendedSet || enabling;

  const onUnattSwitch = (next: boolean) => {
    if (next) {
      // 켜기 = 비밀번호 정하기. 이미 설정돼 있으면 켜진 상태라 여기 오지 않는다.
      setEnabling(true);
      c.setUnattNote(undefined);
      c.setUnattendedPw('');
    } else if (c.unattendedSet) {
      void c.clearUnattended(); // 확인 창을 거친다(useMyConnection)
    } else {
      // 비밀번호를 정하기 전에 다시 끔 = 켜기 취소.
      setEnabling(false);
      c.setUnattendedPw('');
    }
  };

  return (
    <div className="settings">
      {/* 원격 입력 제어(A4) — **세션 중에도 즉시 반영된다.** 끄면 상대는 화면만 계속 볼 수 있다. */}
      <div className="setting">
        <Switch
          label="원격 입력 제어 허용"
          checked={c.controlAllowed}
          onChange={() => void c.toggleControl()}
          disabled={c.controlBusy || c.controlLocked}
        />
        <div className="hint">
          {c.controlLocked
            ? '이 기기는 화면 공유 전용으로 설정돼 있습니다(관리자만 변경 가능).'
            : c.controlAllowed
              ? '끄면 상대는 화면만 볼 수 있습니다. 접속 중에도 바로 적용됩니다.'
              : '지금은 화면만 공유합니다. 상대의 마우스·키보드 입력은 차단됩니다.'}
        </div>
      </div>

      {/* 무인(Easy Access) 접속 — 영속 비밀번호가 있으면 이 PC 앞에 사람이 없어도 접속할 수 있다. On 일 때만 세부가 보인다. */}
      <div className="setting">
        <Switch label="무인 접속 허용" checked={unattOn} onChange={onUnattSwitch} disabled={c.unattendedBusy} />
        {!unattOn && <div className="hint">켜면 이 PC 앞에 사람이 없어도 비밀번호로 원격 접속할 수 있습니다.</div>}
        {unattOn && (
          <div className="setting-body">
            <div className="hint">무인 접속: {c.unattendedSet ? '설정됨' : '비밀번호를 정하면 켜집니다'}</div>

            {/* 미설정(켜는 중) 또는 [변경] = 새 비밀번호를 받는 화면 — 입력칸은 **이때만** 보인다(2026-09-14 사용자 지적). */}
            {(!c.unattendedSet || c.unattMode === 'change') && (
              <div className="row" style={{ gap: 8, marginTop: 6 }}>
                <PasswordField
                  value={c.unattendedPw}
                  onChange={c.setUnattendedPw}
                  placeholder={c.unattendedSet ? '새 무인 비밀번호' : '무인 비밀번호'}
                  aria-label={c.unattendedSet ? '새 무인 비밀번호' : '무인 비밀번호'}
                  style={{ flex: 1, minWidth: 0 }}
                  autoFocus={!c.unattendedSet}
                  onKeyDown={(e) => e.key === 'Enter' && c.setUnattended()}
                />
                <button onClick={c.setUnattended} disabled={c.unattendedBusy || !c.unattendedPw}>
                  {c.unattendedSet ? '변경 저장' : '설정'}
                </button>
                {c.unattendedSet && (
                  <button
                    className="ghost"
                    onClick={() => {
                      c.setUnattMode('idle');
                      c.setUnattendedPw('');
                      c.setUnattNote(undefined);
                    }}
                    disabled={c.unattendedBusy}
                  >
                    취소
                  </button>
                )}
              </div>
            )}

            {/* "내가 아는 값이 맞나" — 구버전에 설정해 [보기] 가 안 되는 기기를 위한 길. */}
            {c.unattendedSet && c.unattMode === 'verify' && (
              <div className="row" style={{ gap: 8, marginTop: 6 }}>
                <PasswordField
                  value={c.unattendedPw}
                  onChange={c.setUnattendedPw}
                  placeholder="확인할 비밀번호"
                  aria-label="확인할 무인 비밀번호"
                  style={{ flex: 1, minWidth: 0 }}
                  onKeyDown={(e) => e.key === 'Enter' && c.verifyUnattended()}
                />
                <button onClick={c.verifyUnattended} disabled={c.unattendedBusy || !c.unattendedPw}>
                  확인
                </button>
                <button
                  className="ghost"
                  onClick={() => {
                    c.setUnattMode('idle');
                    c.setUnattendedPw('');
                    c.setUnattNote(undefined);
                  }}
                  disabled={c.unattendedBusy}
                >
                  취소
                </button>
              </div>
            )}

            {/* 설정돼 있을 때 — 할 수 있는 일만 단추로. */}
            {c.unattendedSet && c.unattMode === 'idle' && (
              <div className="row" style={{ gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {c.unattShown ? (
                  <>
                    <code className="pw-value" style={{ fontSize: 15 }}>
                      {c.unattShown}
                    </code>
                    <button className="ghost" onClick={() => c.setUnattShown(undefined)}>
                      가리기
                    </button>
                    <span className="hint">잠시 뒤 자동으로 가려집니다.</span>
                  </>
                ) : (
                  <>
                    <button className="ghost" onClick={c.showUnattended} disabled={c.unattendedBusy}>
                      비밀번호 보기
                    </button>
                    <button
                      onClick={() => {
                        c.setUnattMode('change');
                        c.setUnattendedPw('');
                        c.setUnattNote(undefined);
                        c.setUnattShown(undefined);
                      }}
                      disabled={c.unattendedBusy}
                    >
                      변경
                    </button>
                    <button className="ghost" onClick={() => void c.clearUnattended()} disabled={c.unattendedBusy}>
                      해제
                    </button>
                  </>
                )}
              </div>
            )}

            {/* "다음부터 묻지 않고 접속" 으로 허용된 계정 — 있을 때만. 무인 비밀번호를 바꾸거나 해제해도 모두 풀린다. */}
            {c.unattendedSet && c.trustedCount > 0 && (
              <div className="row" style={{ gap: 8, marginTop: 6, alignItems: 'center' }}>
                <span className="hint">비밀번호 없이 접속하는 계정 {c.trustedCount}개</span>
                <button
                  className="ghost"
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: `비밀번호 없이 접속하는 계정 ${c.trustedCount}개를 모두 해제할까요?`,
                      message: '해제된 계정은 다음 접속 때 다시 무인 접속 비밀번호를 입력해야 합니다.',
                      confirmLabel: '모두 해제',
                      danger: true,
                    });
                    if (ok) void invoke('clear_trusted_viewers').catch(() => {});
                  }}
                >
                  모두 해제
                </button>
              </div>
            )}

            {c.unattendedSet && c.unattMode === 'idle' && !c.unattShown && (
              <div className="hint" style={{ marginTop: 4 }}>
                이 기기에서만 표시됩니다(서버로 전송되지 않음).{' '}
                <button
                  className="linklike"
                  onClick={() => {
                    c.setUnattMode('verify');
                    c.setUnattendedPw('');
                    c.setUnattNote(undefined);
                  }}
                  disabled={c.unattendedBusy}
                >
                  내가 아는 값이 맞는지 확인
                </button>
              </div>
            )}
            {c.unattNote && (
              <div className="hint" style={{ marginTop: 6, color: c.unattNote.tone === 'warn' ? 'var(--err)' : undefined }}>
                {c.unattNote.text}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 로그인 시 자동 실행(macOS 무인 대기) — 무인 비밀번호와 짝: 비밀번호만 있고 앱이 꺼져 있으면 무인 접속이 안 된다. */}
      {SHELL.autostartSupported && (
        <div className="setting">
          <Switch
            label="로그인하면 자동으로 실행(무인 대기)"
            checked={c.autostart}
            onChange={() => void c.toggleAutostart()}
            disabled={c.autostartBusy}
          />
          <div className="hint">
            {c.autostart
              ? '로그인하면 창 없이 메뉴바에서 대기합니다. 재부팅 후 로그인하기 전에는 접속할 수 없습니다(자동 로그인을 켜 두면 해결).'
              : '끄면 이 앱을 직접 실행해 둔 동안에만 원격지원을 받을 수 있습니다.'}
          </div>
          {c.autostartError && <div className="conn-warn">⚠ {c.autostartError}</div>}
        </div>
      )}

      {/* 등록된 조직(§14.18) — 기기는 여러 조직에 속할 수 있다. 기기 앞 사람이 [탈퇴] 로 뺄 수 있다(확인 받음). */}
      <div className="setting">
        <div className="setting-title">등록 된 조직</div>
        {c.orgs.length === 0 ? (
          <div className="hint" style={{ marginTop: 4 }}>
            등록된 조직이 없습니다.
          </div>
        ) : (
          <ul className="org-list">
            {c.orgs.map((o) => (
              <li key={o.orgId} className="org-item">
                <span className="org-name" title={o.orgName}>
                  {o.orgName || '(이름 없음)'}
                </span>
                <button
                  className="ghost danger"
                  onClick={() => void c.leaveOrg(o)}
                  disabled={!!c.leavingOrgId}
                >
                  {c.leavingOrgId === o.orgId ? '탈퇴 중…' : '탈퇴'}
                </button>
              </li>
            ))}
          </ul>
        )}
        {c.orgNote && (
          <div className={c.orgNote.tone === 'warn' ? 'conn-warn' : 'hint'} style={{ marginTop: 6 }}>
            {c.orgNote.tone === 'warn' ? '⚠ ' : '✓ '}
            {c.orgNote.text}
          </div>
        )}
      </div>

      {/* 조직에 등록(claim) — 등록하면 담당자가 기기 목록에서 이 기기를 보고 바로 접속한다. 수락은 담당자가 한다. */}
      <div className="setting">
        <div className="setting-title">{c.orgs.length > 0 ? '다른 조직에 등록' : '조직에 등록'}</div>
        <div className="row" style={{ gap: 8, marginTop: 6 }}>
          <input
            type="email"
            value={c.claimEmail}
            onChange={(e) => c.setClaimEmail(e.target.value)}
            placeholder="담당자 이메일"
            style={{ flex: 1, minWidth: 0 }}
            onKeyDown={(e) => e.key === 'Enter' && c.requestClaim()}
          />
          <button onClick={c.requestClaim} disabled={c.claimBusy || !c.claimEmail.trim()}>
            {c.claimBusy ? '요청 중…' : '등록 요청'}
          </button>
        </div>
        <div className="hint" style={{ marginTop: 4 }}>
          등록하면 담당자가 기기 목록에서 이 기기를 보고 접속할 수 있습니다(코드를 매번 알려 주지 않아도 됩니다). 담당자가
          수락해야 등록됩니다.
        </div>
        {c.claimNote && (
          <div className={c.claimNote.tone === 'warn' ? 'conn-warn' : 'hint'} style={{ marginTop: 6 }}>
            {c.claimNote.tone === 'warn' ? '⚠ ' : '✓ '}
            {c.claimNote.text}
          </div>
        )}
      </div>
    </div>
  );
}
